import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { filesAPI } from '../services/api';
import { useToast } from '../components/Toast';
import { useAuth } from '../context/AuthContext';

function Drive() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { showToast } = useToast();
  
  const [files, setFiles] = useState([]);
  const [folders, setFolders] = useState([]);
  const [currentFolder, setCurrentFolder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [filterType, setFilterType] = useState('all');
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Verify user is admin
  useEffect(() => {
    if (user && user.role !== 'admin') {
      showToast('Access denied. Admin only.', 'error');
      navigate('/user');
    }
  }, [user, navigate, showToast]);

  useEffect(() => {
    fetchFiles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchFiles = async (folder = null) => {
    try {
      setLoading(true);
      const response = await filesAPI.listFiles(folder);
      setFiles(response.data.files || []);
      setFolders(response.data.folders || []);
      setCurrentFolder(folder);
    } catch (error) {
      console.error('Error fetching files:', error);
      showToast(error.response?.data?.message || 'Failed to fetch files', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteFile = async (filename) => {
    if (!window.confirm(`Are you sure you want to delete "${filename}"?`)) {
      return;
    }

    try {
      await filesAPI.deleteFile(filename, currentFolder);
      showToast('File deleted successfully', 'success');
      setFiles(files.filter(f => f.filename !== filename));
      setSelectedFiles(selectedFiles.filter(f => f !== filename));
    } catch (error) {
      console.error('Error deleting file:', error);
      showToast(error.response?.data?.message || 'Failed to delete file', 'error');
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedFiles.length === 0) {
      showToast('No files selected', 'warning');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete ${selectedFiles.length} file(s)?`)) {
      return;
    }

    try {
      const response = await filesAPI.deleteMultipleFiles(selectedFiles, currentFolder);
      showToast(`Deleted ${response.data.deletedCount} file(s)`, 'success');
      
      if (response.data.failedCount > 0) {
        showToast(`Failed to delete ${response.data.failedCount} file(s)`, 'warning');
      }
      
      // Refresh file list
      await fetchFiles(currentFolder);
      setSelectedFiles([]);
    } catch (error) {
      console.error('Error deleting files:', error);
      showToast(error.response?.data?.message || 'Failed to delete files', 'error');
    }
  };

  const toggleFileSelection = (filename) => {
    if (selectedFiles.includes(filename)) {
      setSelectedFiles(selectedFiles.filter(f => f !== filename));
    } else {
      setSelectedFiles([...selectedFiles, filename]);
    }
  };

  const toggleSelectAll = () => {
    if (selectedFiles.length === filteredAndSortedFiles.length) {
      setSelectedFiles([]);
    } else {
      setSelectedFiles(filteredAndSortedFiles.map(f => f.filename));
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const getFileIcon = (fileType) => {
    switch (fileType) {
      case 'pdf': return '📄';
      case 'image': return '🖼️';
      case 'document': return '📝';
      case 'text': return '📃';
      case 'video': return '🎥';
      default: return '📎';
    }
  };

  // Filter and sort files
  const filteredAndSortedFiles = files
    .filter(file => {
      if (filterType !== 'all' && file.fileType !== filterType) return false;
      if (searchTerm && !file.originalName.toLowerCase().includes(searchTerm.toLowerCase())) return false;
      return true;
    })
    .sort((a, b) => {
      let comparison = 0;
      
      switch (sortBy) {
        case 'name':
          comparison = a.originalName.localeCompare(b.originalName);
          break;
        case 'size':
          comparison = a.size - b.size;
          break;
        case 'type':
          comparison = a.fileType.localeCompare(b.fileType);
          break;
        case 'date':
        default:
          comparison = new Date(a.uploadDate) - new Date(b.uploadDate);
          break;
      }
      
      return sortOrder === 'asc' ? comparison : -comparison;
    });

  const fileTypeCounts = files.reduce((acc, file) => {
    acc[file.fileType] = (acc[file.fileType] || 0) + 1;
    return acc;
  }, {});

  const handleFolderClick = (folderPath) => {
    setSearchTerm('');
    setSelectedFiles([]);
    fetchFiles(folderPath);
  };

  const handleBackClick = () => {
    const parentFolder = currentFolder?.includes('/') 
      ? currentFolder.substring(0, currentFolder.lastIndexOf('/'))
      : null;
    fetchFiles(parentFolder);
  };

  if (loading) {
    return <div className="loading">Loading files...</div>;
  }

  return (
    <div className="container">
      <div className="card">
        <div style={{ marginBottom: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
            <h2 style={{ color: '#667eea', fontSize: '24px', margin: 0 }}>
              📁 File Drive
            </h2>
            {currentFolder && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ color: '#cbd5e0', fontSize: '20px' }}>›</span>
                <button
                  onClick={handleBackClick}
                  style={{
                    padding: '4px 12px',
                    background: '#edf2f7',
                    color: '#667eea',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '13px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  ← Back
                </button>
                <span style={{ color: '#667eea', fontSize: '16px', fontWeight: '600' }}>
                  {currentFolder}
                </span>
              </div>
            )}
          </div>
          <p style={{ color: '#718096', fontSize: '14px', margin: 0 }}>
            {currentFolder ? `Files in ${currentFolder}` : 'Manage all uploaded files'} • {files.length} {files.length === 1 ? 'file' : 'files'} • {formatFileSize(files.reduce((sum, f) => sum + f.size, 0))}
          </p>
        </div>

        {/* Controls */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
          gap: '12px', 
          marginBottom: '20px',
          padding: '16px',
          backgroundColor: '#f7fafc',
          borderRadius: '8px'
        }}>
          {/* Search */}
          <input
            type="text"
            placeholder="🔍 Search files..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              padding: '8px 12px',
              border: '1.5px solid #e2e8f0',
              borderRadius: '6px',
              fontSize: '13px',
              outline: 'none'
            }}
          />

          {/* Filter by type */}
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            style={{
              padding: '8px 12px',
              border: '1.5px solid #e2e8f0',
              borderRadius: '6px',
              fontSize: '13px',
              backgroundColor: 'white'
            }}
          >
            <option value="all">All Types ({files.length})</option>
            <option value="pdf">PDF Files ({fileTypeCounts.pdf || 0})</option>
            <option value="image">Images ({fileTypeCounts.image || 0})</option>
            <option value="video">Videos ({fileTypeCounts.video || 0})</option>
            <option value="document">Documents ({fileTypeCounts.document || 0})</option>
            <option value="text">Text Files ({fileTypeCounts.text || 0})</option>
            <option value="other">Other ({fileTypeCounts.other || 0})</option>
          </select>

          {/* Sort by */}
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            style={{
              padding: '8px 12px',
              border: '1.5px solid #e2e8f0',
              borderRadius: '6px',
              fontSize: '13px',
              backgroundColor: 'white'
            }}
          >
            <option value="date">Sort by Date</option>
            <option value="name">Sort by Name</option>
            <option value="size">Sort by Size</option>
            <option value="type">Sort by Type</option>
          </select>

          {/* Sort order */}
          <button
            onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
            style={{
              padding: '8px 12px',
              border: '1.5px solid #e2e8f0',
              borderRadius: '6px',
              fontSize: '13px',
              backgroundColor: 'white',
              cursor: 'pointer',
              fontWeight: '600'
            }}
          >
            {sortOrder === 'asc' ? '↑ Ascending' : '↓ Descending'}
          </button>
        </div>

        {/* Bulk actions */}
        {selectedFiles.length > 0 && (
          <div style={{
            padding: '12px 16px',
            backgroundColor: '#eef2ff',
            borderRadius: '8px',
            marginBottom: '16px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <span style={{ fontSize: '14px', fontWeight: '600', color: '#667eea' }}>
              {selectedFiles.length} file(s) selected
            </span>
            <button
              onClick={handleDeleteSelected}
              style={{
                padding: '6px 16px',
                backgroundColor: '#fc8181',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontSize: '13px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              🗑️ Delete Selected
            </button>
          </div>
        )}

        {/* Folders */}
        {folders.length > 0 && (
          <div style={{ marginBottom: '24px' }}>
            <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#2d3748', marginBottom: '12px' }}>
              📂 Folders
            </h3>
            <div style={{ 
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
              gap: '12px'
            }}>
              {folders.map((folder) => (
                <div
                  key={folder.path}
                  onClick={() => handleFolderClick(folder.path)}
                  style={{
                    padding: '16px',
                    background: 'white',
                    border: '2px solid #e2e8f0',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = '#667eea';
                    e.currentTarget.style.backgroundColor = '#f7fafc';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = '#e2e8f0';
                    e.currentTarget.style.backgroundColor = 'white';
                  }}
                >
                  <span style={{ fontSize: '32px' }}>📁</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ 
                      fontSize: '14px', 
                      fontWeight: '600', 
                      color: '#2d3748',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>
                      {folder.name}
                    </div>
                    <div style={{ fontSize: '12px', color: '#718096', marginTop: '2px' }}>
                      {folder.fileCount} {folder.fileCount === 1 ? 'file' : 'files'}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Files table */}
        {filteredAndSortedFiles.length === 0 && folders.length === 0 ? (
          <div style={{ 
            textAlign: 'center', 
            padding: '60px 24px', 
            color: '#718096',
            fontSize: '14px'
          }}>
            <p style={{ fontSize: '48px', marginBottom: '12px' }}>📭</p>
            <p style={{ fontSize: '16px', marginBottom: '8px', fontWeight: '600' }}>
              {searchTerm || filterType !== 'all' ? 'No files found' : 'No files uploaded yet'}
            </p>
            <p style={{ fontSize: '14px' }}>
              {searchTerm || filterType !== 'all' ? 'Try adjusting your filters' : 'Upload files when creating quizzes'}
            </p>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: '#f7fafc', borderBottom: '2px solid #e2e8f0' }}>
                  <th style={{ padding: '12px', textAlign: 'left', width: '40px' }}>
                    <input
                      type="checkbox"
                      checked={selectedFiles.length === filteredAndSortedFiles.length}
                      onChange={toggleSelectAll}
                      style={{ cursor: 'pointer', width: '16px', height: '16px' }}
                    />
                  </th>
                  <th style={{ padding: '12px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#4a5568' }}>
                    File Name
                  </th>
                  <th style={{ padding: '12px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#4a5568' }}>
                    Type
                  </th>
                  <th style={{ padding: '12px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#4a5568' }}>
                    Size
                  </th>
                  <th style={{ padding: '12px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#4a5568' }}>
                    Upload Date
                  </th>
                  <th style={{ padding: '12px', textAlign: 'center', fontSize: '13px', fontWeight: '600', color: '#4a5568' }}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredAndSortedFiles.map((file) => (
                  <tr 
                    key={file.filename}
                    style={{ 
                      borderBottom: '1px solid #e2e8f0',
                      transition: 'background-color 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f7fafc'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                  >
                    <td style={{ padding: '12px' }}>
                      <input
                        type="checkbox"
                        checked={selectedFiles.includes(file.filename)}
                        onChange={() => toggleFileSelection(file.filename)}
                        style={{ cursor: 'pointer', width: '16px', height: '16px' }}
                      />
                    </td>
                    <td style={{ padding: '12px', fontSize: '13px', color: '#2d3748' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontSize: '20px' }}>{getFileIcon(file.fileType)}</span>
                        <span style={{ fontWeight: '500', wordBreak: 'break-all' }}>{file.originalName}</span>
                      </div>
                    </td>
                    <td style={{ padding: '12px', fontSize: '13px', color: '#718096' }}>
                      <span style={{
                        padding: '3px 8px',
                        backgroundColor: '#edf2f7',
                        borderRadius: '4px',
                        fontSize: '11px',
                        fontWeight: '600',
                        textTransform: 'uppercase'
                      }}>
                        {file.extension || 'N/A'}
                      </span>
                    </td>
                    <td style={{ padding: '12px', fontSize: '13px', color: '#718096' }}>
                      {formatFileSize(file.size)}
                    </td>
                    <td style={{ padding: '12px', fontSize: '13px', color: '#718096' }}>
                      {new Date(file.uploadDate).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </td>
                    <td style={{ padding: '12px', textAlign: 'center' }}>
                      <button
                        onClick={() => handleDeleteFile(file.filename)}
                        style={{
                          padding: '6px 12px',
                          backgroundColor: '#fff5f5',
                          color: '#c53030',
                          border: '1.5px solid #fc8181',
                          borderRadius: '6px',
                          fontSize: '12px',
                          fontWeight: '600',
                          cursor: 'pointer',
                          transition: 'all 0.2s'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#c53030';
                          e.currentTarget.style.color = 'white';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = '#fff5f5';
                          e.currentTarget.style.color = '#c53030';
                        }}
                      >
                        🗑️ Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Summary */}
        {filteredAndSortedFiles.length > 0 && (
          <div style={{
            marginTop: '20px',
            padding: '16px',
            backgroundColor: '#f7fafc',
            borderRadius: '8px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            fontSize: '13px',
            color: '#4a5568'
          }}>
            <span>
              Showing {filteredAndSortedFiles.length} of {files.length} total files
            </span>
            <span>
              Total size: {formatFileSize(filteredAndSortedFiles.reduce((sum, f) => sum + f.size, 0))}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

export default Drive;
